import { useState } from "react";
import { useQuery } from "react-query";
import { getAllTemplates } from "api/templates";
import { ScaleLoader } from "react-spinners";
import { ArrowLeft, FileText, CheckCircle, AlertCircle } from "lucide-react";

const TEMPLATE_IDS = [3, 1, 6, 7];

const TEMPLATE_DESCRIPTIONS = {
  1: "A structured clinical note capturing patient-reported history, examination findings, clinical diagnosis, and management plan for medical and nursing professionals.",
  3: "A flexible, all-purpose consultation note suitable for any clinical encounter, across any health profession.",
  6: "A structured note for allied health consultations, capturing client history, functional assessments, therapy goals, and treatment plans.",
  7: "A structured clinical note for mental health consultations, incorporating a Mental State Examination, clinical formulation, risk assessment, and therapeutic plan.",
};

// Returns the SOAP template ID to pair with Consult Note (3) based on profession,
// or null if the profession only gets a Consult Note.
const getProfessionSoapTemplateId = (professionId) => {
  if (!professionId) return null;

  // Psychiatrist (21) + Mental Health Professionals (60–64) → Mental Health SOAP Notes (7)
  if (professionId === 21 || (professionId >= 60 && professionId <= 64)) return 7;

  // Allied Health Therapists (45–59) → SOAP Notes Allied Health (6)
  if (professionId >= 45 && professionId <= 59) return 6;
  // Oral Health (77–80)
  if (professionId >= 77 && professionId <= 80) return 6;
  // Orthoptist (84), Hearing Aid Specialist (85)
  if (professionId === 84 || professionId === 85) return 6;
  // NDIS Support Coordinator (89), Aged Care Worker (90), Disability Support Worker (91)
  if (professionId >= 89 && professionId <= 91) return 6;
  // Complementary & Integrative Health (93–95)
  if (professionId >= 93 && professionId <= 95) return 6;

  // Diagnostic & Technical Services (65–76) + Pharmacy Technician (82) → Consult Note only
  if ((professionId >= 65 && professionId <= 76) || professionId === 82) return null;

  // All remaining professions: Medical Practitioners (1–36 excl. 21), Nursing & Midwifery (37–44),
  // Pharmacist (81), Optometrist (83), Paramedics (86–88), ATSI Health Practitioner (92)
  // → SOAP Medical Notes (1)
  return 1;
};

const getVisibleTemplateIds = (professionId) => {
  const soapId = getProfessionSoapTemplateId(professionId);
  return soapId != null ? [3, soapId] : [3];
};

const buildCommonTemplate = (selectedId) => {
  if (selectedId === 4) return "4";
  return `${selectedId},4`;
};

const TemplateCard = ({ id, name, description, isSelected, onSelect }) => (
  <div
    role="radio"
    aria-checked={isSelected}
    tabIndex={0}
    onClick={() => onSelect(id)}
    onKeyDown={(e) => e.key === "Enter" && onSelect(id)}
    className={`cursor-pointer border-2 rounded-xl p-4 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-1 ${
      isSelected
        ? "border-brand-purple bg-purple-50/60"
        : "border-gray-200 hover:border-brand-purple/40 bg-white"
    }`}
  >
    <div className="flex items-start gap-3">
      <div
        className={`p-2 rounded-lg shrink-0 mt-0.5 ${
          isSelected ? "bg-brand-purple/10" : "bg-gray-100"
        }`}
      >
        <FileText
          className={`w-4 h-4 ${isSelected ? "text-brand-purple" : "text-gray-500"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[14px] font-semibold text-gray-900 leading-tight">
            {name}
          </p>
          {isSelected && (
            <CheckCircle className="w-[18px] h-[18px] text-brand-purple shrink-0" />
          )}
        </div>
        <p className="text-[12px] text-gray-500 mt-1 leading-snug">{description}</p>
      </div>
    </div>
  </div>
);

const TemplateStep = ({ onSubmit, onSkip, onBack, isLoading, professionId }) => {
  const visibleTemplateIds = getVisibleTemplateIds(professionId);
  const [selectedId, setSelectedId] = useState(3);

  const { data: allTemplates, isLoading: isLoadingTemplates, isError, refetch } = useQuery(
    "templates",
    getAllTemplates
  );

  const templates = visibleTemplateIds
    .map((id) => {
      const found = allTemplates?.find((t) => Number(t.id) === id);
      return found ? { ...found, id: Number(found.id), description: TEMPLATE_DESCRIPTIONS[id] } : null;
    })
    .filter(Boolean);

  const handleSubmit = () => {
    onSubmit(buildCommonTemplate(selectedId));
  };

  return (
    <>
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-[12px] text-gray-400 hover:text-gray-600 transition-colors mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back
      </button>

      {/* Progress bar */}
      <div className="flex items-center gap-2 mb-7">
        <div className="h-1.5 flex-1 rounded-full bg-brand-purple" />
        <div className="h-1.5 flex-1 rounded-full bg-brand-purple" />
        <span className="text-[11px] text-gray-400 shrink-0 ml-1">2 of 2</span>
      </div>

      {/* Icon */}
      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center mb-4">
        <FileText className="w-5 h-5 text-brand-purple" />
      </div>

      <h2 className="text-[22px] font-bold text-navy mb-1 leading-tight">
        Select your preferred note template
      </h2>
      <p className="text-[13px] text-gray-500 mb-5">
        Select the primary note for your sessions. Your notes are auto-generated after every session.
      </p>

      {/* Template cards */}
      {isLoadingTemplates ? (
        <div className="flex justify-center py-8">
          <ScaleLoader color="#7B5EA7" height={20} />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-8 mb-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <p className="text-[13px] text-gray-500">
            Couldn't load templates. Please check your connection.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-[13px] text-brand-purple font-medium hover:underline"
          >
            Try again
          </button>
        </div>
      ) : (
        <div
          role="radiogroup"
          aria-label="Choose a note template"
          className="flex flex-col gap-3 mb-6"
        >
          {templates.map(({ id, name, description }) => (
            <TemplateCard
              key={id}
              id={id}
              name={name}
              description={description}
              isSelected={selectedId === id}
              onSelect={setSelectedId}
            />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isLoading || isLoadingTemplates}
        aria-busy={isLoading}
        className="relative w-full h-[46px] rounded-[10px] bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[15px] font-bold hover:from-pink-600 hover:to-purple-700 hover:-translate-y-px transition-all duration-200 shadow-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:translate-y-0"
      >
        <span className={isLoading ? "opacity-0" : "opacity-100"}>
          Set Up My Notes →
        </span>
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <ScaleLoader height={16} color="white" />
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={onSkip}
        disabled={isLoading}
        className="w-full mt-3 text-[13px] text-gray-400 hover:text-gray-600 transition-colors disabled:cursor-not-allowed"
      >
        Skip, use default
      </button>
    </>
  );
};

export default TemplateStep;
